
import { CommonStore, FloweringStore } from '@/store/modules';
import { Component, Vue } from 'vue-property-decorator';
import { date, QTable } from 'quasar';
import helpers from '@/utils/helpers';
import { YieldByWeekTransposedData } from '@/store/models/flowering';

@Component
export default class YieldByWeek extends Vue {
  $refs!: {
    table1: InstanceType<typeof QTable>;
    table2: InstanceType<typeof QTable>;
    table3: InstanceType<typeof QTable>;
  };
  loadingHarvestDate = false;

  loading = false;

  filter = '';
  pagination: {
    sortBy: string;
    descending: boolean;
    page: number;
    rowsPerPage: number;
  } = {
    sortBy: 'farmName',
    descending: false,
    page: 1,
    rowsPerPage: 0
  };

  eventNumOptions = [
    { value: null, label: 'All' },
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' }
  ];
  eventNum: number | null = null;
  totalWeeks = 12;

  // eslint-disable-next-line
  tables: any[] = [];

  thumbStyle = {
    right: '4px',
    borderRadius: '5px',
    backgroundColor: '#bbb',
    height: '7px',
    opacity: 0.75
  };

  barStyle = {
    right: '2px',
    borderRadius: '9px',
    backgroundColor: '#bbb',
    height: '7px',
    opacity: 0.2
  };

  // eslint-disable-next-line
  maximumPercentTerminalsFlowered(row: any) {
    const maxValList = [];
    let maxVal = 100;

    for (let week = 0; week < this.totalWeeks; week++) {
      if (row[`w${week + 1}`] && !isNaN(row[`w${week + 1}`])) {
        maxValList.push(parseInt(row[`w${week + 1}`]));
      }
    }
    maxVal = Math.max(...maxValList);
    return maxVal;
  }

  getWeekOfYear(dt: string) {
    if (dt == '') {
      return '';
    }
    const dateObj = date.extractDate(dt, 'YYYY-MM-DD');
    return date.formatDate(dateObj, 'w');
  }

  get currentFarm() {
    return CommonStore.SelectedFarmBlock;
  }

  get startDate() {
    return CommonStore.StartDate;
  }

  get endDate() {
    return CommonStore.EndDate;
  }

  // get blocksWithFruitCount() {
  //   return BlockStore.BlocksWithFruitCount;
  // }

  get yieldByWeekTransposed() {
    return FloweringStore.YieldByWeekTransposed;
  }

  get yieldByWeekData() {
    if (this.yieldByWeekTransposed && this.yieldByWeekTransposed.data) {
      return this.yieldByWeekTransposed.data;
    }
    return [];
  }

  get yieldByWeekDates() {
    if (this.yieldByWeekTransposed && this.yieldByWeekTransposed.header) {
      return this.yieldByWeekTransposed.header.filter(
        x => !['block', 'blockName', 'farmName', 'farm'].includes(x)
      );
    }
    return [];
  }

  get farmCropBlock() {
    if (this.yieldByWeekData) {
      const farmCropBlock: {
        [key: string]: Set<string>;
      } = {};
      for (const data of this.yieldByWeekData) {
        if (farmCropBlock[data.farmName]) {
          farmCropBlock[data.farmName].add(data.cropVarietyName);
        } else {
          farmCropBlock[data.farmName] = new Set();
          farmCropBlock[data.farmName].add(data.cropVarietyName);
        }
      }
      const data: any = [];
      for (const farm of Object.keys(farmCropBlock)) {
        data.push({
          type: 'farm',
          farm: farm,
          farmName: farm,
          cropVariety: 0,
          cropVarietyName: '',
          block: 0,
          blockName: '',
          fruitsCount: 0,
          fruitWeight: 0
        });

        const farmData = this.yieldEstimation.filter(x => x.farmName == farm);
        for (const crop of farmCropBlock[farm].keys()) {
          data.push({
            type: 'crop',
            farm: 0,
            farmName: '',
            cropVariety: 0,
            cropVarietyName: crop,
            block: 0,
            blockName: '',
            fruitsCount: 0,
            fruitWeight: 0
          });
          const farmCropData = farmData.filter(x => x.cropVarietyName == crop);
          data.push(...farmCropData);
          const subTotal: any = {
            type: 'sub_total',
            farm: 0,
            farmName: '',
            cropVariety: 0,
            cropVarietyName: '',
            block: 0,
            blockName: 'Sub Total',
            fruitsCount: 0,
            fruitWeight: 0,
            area: farmCropData
              .map(x => x.area)
              .reduce(
                (previousValue, currentValue) => previousValue + currentValue
              )
              .toFixed(2)
          };
          for (const header of this.yieldByWeekDates) {
            subTotal[header] = farmCropData
              .map(x => x[header])
              .reduce(
                (previousValue, currentValue) => previousValue + currentValue
              );
          }
          data.push(subTotal);
        }

        const farmTotal: any = {
          type: 'farm_total',
          farm: 0,
          farmName: '',
          cropVariety: 0,
          cropVarietyName: '',
          block: 0,
          blockName: 'Farm Total',
          fruitsCount: 0,
          fruitWeight: 0,
          area: farmData
            .map(x => x.area)
            .reduce(
              (previousValue, currentValue) => previousValue + currentValue
            )
            .toFixed(2)
        };
        for (const header of this.yieldByWeekDates) {
          farmTotal[header] = farmData
            .map(x => x[header])
            .reduce(
              (previousValue, currentValue) => previousValue + currentValue
            );
        }
        data.push(farmTotal);
      }

      if (this.yieldEstimation && this.yieldEstimation.length) {
        const grandTotal: any = {
          type: 'grand_total',
          farm: 0,
          farmName: '',
          cropVariety: 0,
          cropVarietyName: '',
          block: 0,
          blockName: 'Grand Total',
          fruitsCount: 0,
          fruitWeight: 0,
          area: this.yieldEstimation
            .map(x => x.area)
            .reduce(
              (previousValue, currentValue) => previousValue + currentValue
            )
            .toFixed(2)
        };
        for (const header of this.yieldByWeekDates) {
          grandTotal[header] = this.yieldEstimation
            .map(x => x[header])
            .reduce(
              (previousValue, currentValue) => previousValue + currentValue
            );
        }
        data.push(grandTotal);
      }
      return data;
    }
    return [];
  }

  get rawFloweringHarvestDate() {
    return FloweringStore.RawFloweringHarvestDate;
  }
  get tableColumn() {
    let columns = [
      {
        name: 'farmName',
        label: 'Farm',
        field: 'farmName',
        sortable: true,
        hidden: true
      },
      {
        name: 'cropVarietyName',
        label: 'Crop Variety',
        field: 'cropVarietyName',
        sortable: true,
        hidden: true
      },
      {
        name: 'blockName',
        label: 'Block',
        field: 'blockName',
        sortable: true,
        hidden: false
      },
      {
        name: 'area',
        label: 'Area',
        field: 'area',
        sortable: true,
        hidden: false
      }
    ];
    if (this.yieldByWeekTransposed && this.yieldByWeekTransposed.header) {
      const transposedFloweringColumns = this.yieldByWeekTransposed.header
        .filter(
          x =>
            x &&
            x != null &&
            !['block', 'blockName', 'farmName', 'farm'].includes(x)
        )
        .map((x, index) => {
          return {
            name: x,
            label: x,
            field: x,
            sortable: x == 'blockName' ? true : false,
            hidden: false
          };
        });
      columns = [...columns, ...transposedFloweringColumns];
    }
    return columns;
  }

  get yieldEstimation() {
    if (this.yieldByWeekData && this.yieldByWeekData.length > 0) {
      const data = this.yieldByWeekData.map(x => {
        return {
          ...x,
          maxPercentTerminalsValue: this.maximumPercentTerminalsFlowered(x)
        };
      });

      // eslint-disable-next-line
      return data.map((x: any) => {
        const dt: {
          block: number;
          blockName: string;
          farm: number;
          farmName: string;
          cropVariety: number;
          cropVarietyName: string;
          // eslint-disable-next-line
          [key: string]: any;
        } = {
          type: 'value',
          farm: x.farm,
          farmName: x.farmName,
          cropVariety: x.cropVariety,
          cropVarietyName: x.cropVarietyName,
          block: x.block,
          blockName: x.blockName,
          area: x.area,
          fruitsCount: x.fruitsCount,
          fruitWeight: x.fruitWeight
        };

        for (let week = 0; week < this.totalWeeks; week++) {
          let val = parseInt(
            (
              (x[`mzw${week + 1}`] / x.maxPercentTerminalsValue) *
              (x.fruitsCount ? x.fruitsCount : 0)
            ).toFixed(0)
          );
          if (!val || isNaN(val)) {
            val = 0;
          }
          // dt[`mzw${week + 1}`] = val;
          dt[x[`wd${week + 1}`]] = val;
        }
        for (const header of this.yieldByWeekDates) {
          if (!(header in dt)) {
            dt[header] = 0;
          }
        }
        return dt;
      });
    }
    return [];
  }

  get harvestCapacityQuantity() {
    if (this.firstFruitCount && this.firstFruitCount.harvestCapacityThreshold) {
      return (
        (this.firstFruitCount.harvestCapacity / 100) *
        this.firstFruitCount.harvestCapacityThreshold
      );
    }
    return 0;
  }

  get firstFruitCount() {
    if (this.yieldByWeekData && this.yieldByWeekData.length > 0) {
      return this.yieldByWeekData[0];
    }
    return {} as YieldByWeekTransposedData;
  }

  checkTarget(val: number | string) {
    if (val == 'Total' || this.yieldByWeekData.length <= 0) {
      return '';
    }
    if (
      val >
      this.firstFruitCount.harvestCapacity + this.harvestCapacityQuantity
    ) {
      return 'bg-red-1';
    } else if (
      val <
      this.firstFruitCount.harvestCapacity - this.harvestCapacityQuantity
    ) {
      return 'bg-blue-1';
    }
    return 'bg-green-1';
  }

  //eslint-disable-next-line
  async onRequest(props: any) {
    const { page, rowsPerPage, sortBy, descending } = props.pagination;
    const filter = props.filter;
    this.loading = true;

    const ordering = `${descending ? '-' : ''}${sortBy}`;

    const queryParams = {
      page: page,
      search: filter,
      eventNum: this.eventNum,
      ordering: ordering,
      pageSize: rowsPerPage,
      // farm: this.currentFarm.farmId,
      farm: undefined,
      dateAfter: this.startDate,
      dateBefore: this.endDate
    };
    // BlockStore.getAllMyBlocksWithFruitCounts(queryParams);
    this.loadingHarvestDate = true;
    // FloweringStore.getRawFloweringHarvestDate(queryParams).then(() => {
    //   this.loadingHarvestDate = false;
    // });
    await FloweringStore.getYieldByWeekTransposed(queryParams);
    this.loading = false;
  }

  // eslint-disable-next-line
  scrollHandler(event: any) {
    window.requestAnimationFrame(() => {
      const scrollTop = event.target.scrollTop;
      const scrollLeft = event.target.scrollLeft;

      for (const table of this.tables) {
        table.scrollTop = scrollTop;
        table.scrollLeft = scrollLeft;
      }
    });
  }

  async loadRequest() {
    this.loading = true;
    await this.onRequest({
      pagination: this.pagination,
      filter: this.filter
    });
    this.loading = false;
  }
  async beforeCreate() {
    await helpers.fetchingDefaultFarm();
    this.loadRequest();
  }

  mounted() {
    const tables = document.getElementsByClassName('q-table__middle scroll');
    for (const table of tables) {
      table.addEventListener('scroll', this.scrollHandler);
      this.tables.push(table);
    }
  }
}
